import React from 'react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useIsCardShell } from '@/components/layout/cardShell/shellVariant';

/**
 * The single loading state for the Inbox (`/home/channels/:channel`).
 *
 * Opening the inbox used to flash two spinners before the list appeared:
 *   1. a bare spinner — the route-level `<Suspense>` fallback in `App.tsx`
 *      while the `ChannelChat` chunk downloads
 *   2. a full-page "Loading..." — the `FeatureRoute` gate waiting on
 *      entitlements
 * and only then the conversation list's own row skeleton. Both now render
 * this skeleton, which draws the same columns the loaded inbox does, so the
 * layout appears once and the rows are the only thing that changes.
 */

/** Conversation rows — shared with `OptimizedConversationList` so both match. */
export const ConversationListRowsSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="p-4 space-y-3" data-testid="inbox-list-rows-skeleton">
    {[...Array(rows)].map((_, i) => (
      <Card key={i} className="p-3 border-[#edf1f7]">
        <div className="flex items-start space-x-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4 rounded" />
            <Skeleton className="h-3 w-1/2 rounded" />
          </div>
        </div>
      </Card>
    ))}
  </div>
);

/** Account title, All / Mine / Unassigned tabs, search and filter. */
const ConversationListHeaderSkeleton: React.FC = () => (
  <div className="px-4 pt-4 pb-2 flex-shrink-0">
    <div className="flex h-7 items-center gap-2.5 mb-2.5">
      <Skeleton className="w-6 h-6 rounded-full shrink-0" />
      <Skeleton className="h-5 w-32" />
    </div>
    <div className="-mx-4 px-4 flex items-center gap-6 border-b border-[#edf1f7]">
      {[20, 32, 72].map((w) => (
        <div key={w} className="pb-2.5 pt-2">
          <Skeleton className="h-3.5" style={{ width: w }} />
        </div>
      ))}
    </div>
    <div className="flex gap-2 mt-3">
      <Skeleton className="h-10 flex-1 rounded-lg" />
      <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
    </div>
  </div>
);

/**
 * The page-level placeholder, used by the route guard and the `<Suspense>`
 * fallback. Mirrors the outer layout of `Inbox` in both shells; the message
 * pane stays blank because a deep link may be about to open a chat there.
 */
export const InboxPageSkeleton: React.FC = () => {
  const isCardShell = useIsCardShell();
  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-1 flex-col overflow-hidden',
        isCardShell ? 'bg-white' : 'bg-[#f4f6fa]',
      )}
      aria-busy="true"
      aria-label="Loading inbox"
      data-testid="inbox-page-skeleton"
    >
      <div
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden',
          isCardShell ? 'gap-0 p-0' : 'gap-0 sm:gap-4 p-0 sm:p-4',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col lg:w-[350px] flex-shrink-0 bg-white overflow-hidden',
            isCardShell
              ? 'lg:border-r lg:border-[#EAECF0]'
              : 'sm:rounded-[14px] sm:border sm:border-[#edf1f7] sm:shadow-[0_1px_3px_rgba(16,24,40,0.04)]',
          )}
        >
          <ConversationListHeaderSkeleton />
          <ConversationListRowsSkeleton />
        </div>
        <div
          className={cn(
            'hidden lg:flex flex-1 min-w-0 overflow-hidden bg-white',
            !isCardShell && 'sm:rounded-[14px] sm:border sm:border-[#edf1f7] sm:shadow-[0_1px_3px_rgba(16,24,40,0.04)]',
          )}
        />
      </div>
    </div>
  );
};

export default InboxPageSkeleton;
